import { Divider, Grid } from '@mui/material';
import { memo, useCallback } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { CohabitantFacto } from 'mutation/view-models/resolution-facto-cohabitation';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import FormGenerality from 'shell/components/shared/form-generality/form';
import { SubjectData } from 'shell/models/person';
import { noop } from 'shell/utils/noop';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { DatePicker } from '@mui/lab';
import MunicipalityList from 'shell/components/shared/municipality-list';
import PmiTextField from 'shell/components/shared/text-field';

interface AccordionCohabitantFormProps {
  disabled?: boolean;
  values?: CohabitantFacto;
  onChange: (key: keyof CohabitantFacto, value: CohabitantFacto[keyof CohabitantFacto]) => void;
  onChangeValues: (values?: CohabitantFacto, convivente?: unknown) => void;
  touched?: FormikTouched<CohabitantFacto>;
  errors?: FormikErrors<CohabitantFacto>;
  idFamilyIstat?: string;
  idFamilyMunicipality?: string;
  setFieldValue: (key: string, value?: SubjectData) => void;
}

const AccordionCohabitantFormComponent = ({
  disabled,
  values,
  onChange,
  touched,
  errors,
}: AccordionCohabitantFormProps): React.ReactElement => {
  const onFieldPersonChange = useCallback(
    (key, value) => {
      onChange('person', {
        ...(values?.person || {}),
        [key]: value,
      });
    },
    [values?.person, onChange]
  );

  const onFieldGeneralityChange = useCallback(
    (key, value) => {
      onChange('generality', {
        ...(values?.generality || {}),
        [key]: value,
      });
    },
    [values?.generality, onChange]
  );

  const onChangeFieldsGenerality = useCallback(
    (vals) => {
      onChange('generality', {
        ...(values?.generality || {}),
        ...vals,
      });
    },
    [values?.generality, onChange]
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <FormGenerality
          disabled={disabled}
          onChangePerson={onFieldPersonChange}
          onChangeGenerality={onFieldGeneralityChange}
          onChangeFieldsGenerality={onChangeFieldsGenerality}
          person={values?.person}
          onChangeFieldsPerson={noop}
          generality={values?.generality}
          errorsGenerality={errors?.generality as FormikErrors<AccordionGeneralityForm>}
          errorsPerson={errors?.person as FormikErrors<AccordionPersonForm>}
          touchedPerson={touched?.person as unknown as FormikTouched<AccordionPersonForm>}
          touchedGenerality={
            touched?.generality as unknown as FormikTouched<AccordionGeneralityForm>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Convivenza di fatto</PmiTitleDivider>
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data inizio"
          aria-label="Data inizio"
          value={values?.contractStipulation?.dateStart || null}
          onChange={(date) => {
            onChange('dateStart', date);
          }}
          disabled
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateStart"
              fullWidth
              error={touched?.dateStart && Boolean(errors?.dateStart)}
              helperText={errors?.dateStart}
            />
          )}
        />
      </Grid>
      <Grid item container xs={12} spacing={1}>
        <Grid item xs={12} md={4}>
          <MunicipalityList
            label="Comune registrazione convivenza"
            aria-label="Comune registrazione convivenza"
            value={values?.contractStipulation?.municipality || null}
            disabled
            onChange={(element) => {
              onChange('municipalityPlace', element);
            }}
            error={touched?.municipalityPlace && Boolean(errors?.municipalityPlace)}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            label="Provincia"
            inputProps={{
              maxLength: '2',
            }}
            aria-label="Provincia"
            fullWidth
            value={values?.contractStipulation?.municipality?.province || ''}
            disabled
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

AccordionCohabitantFormComponent.displayName = 'Accordion cohabitant form component';

const AccordionCohabitantForm = memo(AccordionCohabitantFormComponent);

export default AccordionCohabitantForm;
